import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { ArrowLeft, ExternalLink, Award } from "lucide-react";

/**
 * Public SBE Profile — Small Business Enterprise capability card.
 * Used by Prime Suppliers to preview an SBE during Business Matchmaking.
 * Route: /sbe/:id  ·  Endpoint: GET /api/sbe/{id}
 */
export default function SBEProfile() {
  const { id } = useParams();
  const [sbe, setSbe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/sbe/${id}`)
      .then(({ data }) => setSbe(data.sbe))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading SBE profile…</div></section></Layout>;
  }

  if (error || !sbe) {
    return (
      <Layout>
        <section className="container-summit py-24 max-w-xl">
          <h1 className="serif text-3xl">SBE profile unavailable</h1>
          <p className="mt-3 text-zinc-500">This Small Business Enterprise hasn&apos;t published a public profile yet.</p>
          <Link to="/matchmaking" className="btn-black mt-8" data-testid="sbep-back-to-matchmaking">
            Back to Matchmaking
          </Link>
        </section>
      </Layout>
    );
  }

  const websiteHref = sbe.website
    ? (/^https?:\/\//.test(sbe.website) ? sbe.website : `https://${sbe.website}`)
    : null;

  return (
    <Layout>
      <section className="border-b border-zinc-200 bg-white">
        <div className="container-summit py-14 md:py-20 max-w-4xl">
          <Link to="/matchmaking" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black"
                data-testid="sbep-back-link">
            <ArrowLeft size={12} /> Back to Matchmaking
          </Link>

          <div className="mt-8 flex items-center gap-6 flex-wrap">
            <div>
              <div className="overline"><span className="accent-rule" />Small Business Enterprise</div>
              <h1 className="serif text-4xl md:text-5xl mt-3" data-testid="sbep-company">{sbe.company || sbe.name}</h1>
              {websiteHref && (
                <a href={websiteHref} target="_blank" rel="noreferrer"
                   data-testid="sbep-website"
                   className="mt-3 inline-flex items-center gap-1 text-sm text-[#A39171] hover:text-black underline underline-offset-4">
                  {sbe.website.replace(/^https?:\/\//, "").replace(/^www\./, "")}
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="container-summit py-12 md:py-16 max-w-4xl grid gap-10">
        {sbe.description && (
          <Block label="Company Description" testId="sbep-description">
            <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{sbe.description}</p>
          </Block>
        )}

        {sbe.products_services && (
          <Block label="Products & Services" testId="sbep-products">
            <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{sbe.products_services}</p>
          </Block>
        )}

        {sbe.industries_served?.length > 0 && (
          <Block label="Industries Served" testId="sbep-industries-served">
            <div className="flex flex-wrap gap-2">
              {sbe.industries_served.map((a) => (
                <span key={a} className="text-[11px] uppercase tracking-[0.18em] border border-black px-3 py-1.5">
                  {a}
                </span>
              ))}
            </div>
          </Block>
        )}

        {sbe.matchmaking_preferences?.length > 0 && (
          <Block label="Business Matchmaking Interests" testId="sbep-matchmaking">
            <div className="text-xs text-zinc-500 mb-3">
              Sourcing categories this SBE is pursuing with Prime Suppliers.
            </div>
            <div className="flex flex-wrap gap-2">
              {sbe.matchmaking_preferences.map((a) => (
                <span key={a} className="text-[11px] uppercase tracking-[0.18em] border border-[#A39171] text-[#7A6A4A] px-3 py-1.5">
                  {a}
                </span>
              ))}
            </div>
          </Block>
        )}

        {sbe.certifications?.length > 0 && (
          <Block label="Business Certifications" testId="sbep-certifications">
            <div className="text-xs text-zinc-500 mb-3">Self-attested by the SBE at registration.</div>
            <ul className="grid sm:grid-cols-2 gap-y-2 text-[15px] text-zinc-700">
              {sbe.certifications.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <Award size={14} className="text-[#A39171] shrink-0 mt-1" strokeWidth={1.6} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {sbe.capability_statement_file_id && (
          <Block label="Capability Statement" testId="sbep-capability">
            <a href={`${BACKEND_URL}/api/files/${sbe.capability_statement_file_id}`}
               target="_blank" rel="noreferrer"
               data-testid="sbep-capability-link"
               className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.18em] border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors">
              Download Capability Statement <ExternalLink size={12} />
            </a>
          </Block>
        )}

        {(sbe.name || sbe.title) && (
          <Block label="Primary Contact" testId="sbep-contact">
            <div className="text-[15px] text-black">{sbe.name || "—"}</div>
            {sbe.title && <div className="text-sm text-zinc-500 mt-0.5">{sbe.title}</div>}
          </Block>
        )}

        <div className="border-t border-zinc-200 mt-8 pt-6 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          Profile populated from SBE registration.
        </div>
      </section>
    </Layout>
  );
}

function Block({ label, testId, children }) {
  return (
    <section data-testid={testId}>
      <div className="overline mb-4">{label}</div>
      {children}
    </section>
  );
}
